export type IndexStringPair = [number, string];

type LatamChords = 'do' | 're' | 'mi' | 'fa' | 'sol' | 'la' | 'si';
type AmericanChords = 'c' | 'd' | 'e' | 'f' | 'g' | 'a' | 'b';


export interface SongLine {
    isTab: boolean;
    content: string;
    tabs: IndexStringPair[];
}

export class GuitarChordParse {


    private chords: string[] = [];
    private rawSong: string = '';
    private tabs: { [idx: number]: IndexStringPair[] } = {};
    private lines: string[] = [];
    private mainChordsLatam: LatamChords[] = [
        'do', 're', 'mi', 'fa', 'sol', 'la', 'si',
    ];
    private mainChords: string[] = [
        'do', 're', 'mi', 'fa', 'sol', 'la', 'si',
        'c', 'd', 'e', 'f', 'g', 'a', 'b',
    ];
    private ignoreWords: string[] = [
        '', '|', '[', ']', ' ', '%', 'x', '-',
    ];
    private americanizeArr = {
        do: 'C', re: 'D', mi: 'E', fa: 'F', sol: 'G', la: 'A', si: 'B',
    };

    public getChords(): string[] {
        return this.chords;
    }

    public loadSong(text: string) {
        this.rawSong = text;
        this.tabs = {};
        this.chords = [];
        this.lines = [];
    }

    public transformSong(): SongLine[] {
        this.parseSong();
        const lines = [];
        const len = this.lines.length;
        let line;
        let i;

        for (i = 0; i < len; i++) {
            line = this.cleanTabu(this.lines[i]);
            const tabs: IndexStringPair[] = [];
            const lineObj = {isTab: !!this.tabs[i], content: line, tabs};
            if (lineObj.isTab) {
                lineObj.tabs = this.tabs[i];
            }
            lines.push(lineObj);
        }
        return lines;
    }


    private splitWithIndex(str: string, delim: string): IndexStringPair[] {
        const ret: IndexStringPair[] = [];
        const splits = str.split(delim);
        let index = 0;
        for (const el of splits) {
            ret.push([index, el]);
            index += el.length + delim.length;
        }
        return ret;
    }

    private americanize(tokenOrig: string): string {
        tokenOrig = tokenOrig.replace(/[\])}[{(]/g, '');
        const token = tokenOrig.toLowerCase().trim();
        const hasVars = /\//.test(token);
        if (hasVars) {
            const chords = tokenOrig.split('/');
            return this.americanize(chords[0]) + '/' + this.americanize(chords[1]);
        }


        for (let i = 0; i < this.mainChordsLatam.length; i++) {
            const idx = token.indexOf(this.mainChordsLatam[i]);
            if (idx !== 0) {
                continue;
            }
            const american = this.americanizeArr[this.mainChordsLatam[i]];

            const second = token.slice(this.mainChords[i].length).trim();
            if (second === '') {
                return american;
            }
            const matches = second.match(/(#|b|&)?m?7?(sus|add|maj|dim|inv)?[0-9]?/);
            if (matches && (matches[0] !== '' && matches[0] === second)) {
                return american + second;
            }
        }
        return tokenOrig;
    }

    private isTab(token: string): boolean {

        token = token.toLowerCase().trim();

        const hasVars = /\//.test(token);
        if (hasVars) {
            const chords = token.split('/');
            return this.isTab(chords[0]) && this.isTab(chords[1]);
        }
        // ignore parentheses
        token = token.replace(/[\])}[{(]/g, '');
        for (const chord of this.mainChords) {
            const idx = token.indexOf(chord);
            if (idx !== 0) {
                continue;
            }

            const second = token.slice(chord.length).trim();
            if (second === '') {
                return true;
            }
            let matches = second.match(/(#|b|&)?7?b??(sus|add|maj|dim|inv|aug)?[0-9]?/);
            if (matches && (matches[0] !== '' && matches[0] === second)) {
                return true;
            }
            matches = second.match(/(#|b|&)?m?7?b?(sus|add|maj|dim|inv|aug)?[0-9]?/);
            if (matches && (matches[0] !== '' && matches[0] === second)) {
                return true;
            }
        }
        return false;
    }


    private extractTabs(line: string): IndexStringPair[] {

        // /([CF]♯?|[ABDEG]♭?)(dim|dom7|+|°|ø7?$|aug|M)?m?(aj|in)?6?7?$/
        const tokens: IndexStringPair[] = this.splitWithIndex(line, ' ');
        let detectedWords = 0;
        let tabs = [];
        for (const token of tokens) {
            if (this.isTab(token[1])) {
                token[2] = token[1];
                token[1] = this.americanize(token[1]);
                tabs.push(token);
            } else if (this.ignoreWords.indexOf(token[1]) === -1) {
                detectedWords++;
            }
        }

        if (detectedWords >= tabs.length) {
            tabs = [];
        }

        return tabs;
    }


    private repeatString(str: string, num: number) {
        return new Array(num + 1).join(str);
    }


    private cleanTabu(line: string) {
        let res = line.replace(/^\t/g, this.repeatString(' ', 10));
        let i = res.search(/\t/);
        while (i >= 0) {
            res = res.substr(0, i) + this.repeatString(' ', 8 - ((i + 6) % 8)) + res.substr(i + 1);
            i = res.search(/\t/);
        }
        return res;

    }

    private splitLines() {
        return this.rawSong.split(/\r\n|\r|\n/g);
    }

    private saveTabs(tabs: IndexStringPair[]) {
        let j;
        let i;
        for (j = 0; j < tabs.length; j++) {
            let exists = false;
            for (i = 0; i < this.chords.length; i++) {

                if (this.chords[i].toLowerCase() === tabs[j][1].toLowerCase()) {
                    exists = true;
                }
            }
            if (!exists) {
                this.chords.push(tabs[j][1]);
            }
        }
    }

    private parseSong() {
        this.lines = this.splitLines();
        this.tabs = {};
        this.chords = [];
        const len = this.lines.length;
        let i;
        let line;
        let tabs;
        for (i = 0; i < len; i++) {
            line = this.lines[i];
            tabs = this.extractTabs(this.cleanTabu(line));
            if (tabs && tabs.length) {
                this.tabs[i] = tabs;
                this.saveTabs(tabs);
            }
        }
        return this;
    }

}


